/**
 * @file src/sync/debugSync.js
 * @description Dev-only debug seam for the quantize/tempo audio path (decision 005: no window
 * globals as seams). Replaces `window.__orbitersDebugSync` (verbose quantize/tempo console
 * logging). A plain module, not a window property — reachable from devtools via
 * `import('/src/sync/debugSync.js').then(m => m.setDebugSyncLogging(true))`. Nothing in production
 * ever calls the setters.
 */
import { voiceRegistry } from '../voice/VoiceRegistry.js';

let loggingEnabled = false;
let driftCorrectionDisabled = false;

export function isDebugSyncLoggingEnabled() {
  return loggingEnabled;
}

export function setDebugSyncLogging(on) {
  loggingEnabled = on === true;
}

export function isDriftCorrectionDisabled() {
  return driftCorrectionDisabled;
}

/**
 * Kill switch for the shared-timecode CORRECTION ladder. Measurement stays on, so the drift number
 * remains observable while the audio is left alone. Engaging it drops every voice's live rate trim —
 * a correction must never stay baked into what is playing after the thing that applied it is off.
 */
export function setDriftCorrectionDisabled(on) {
  driftCorrectionDisabled = on === true;
  if (!driftCorrectionDisabled) return;
  for (const record of voiceRegistry.all()) {
    try { record?.audioEngine?.clearDriftCorrection?.(); } catch (_) {}
  }
}

/**
 * Per-voice drift readout: `[{ voiceId, lastMs, medianMs, trim, sampling }]`. This module keeps NO
 * state of its own — each voice's engine owns its numbers and this only reads them, so the readout
 * can never disagree with what is actually driving the audio.
 */
export function getDriftReadout() {
  return voiceRegistry.all()
    .filter((record) => typeof record?.audioEngine?.getDriftState === 'function')
    .map((record) => ({ voiceId: record.id ?? null, ...record.audioEngine.getDriftState() }));
}
